import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Button } from '@xxtract/ui';
import type { LinkWeigering } from '@/components/tabel/useStartadres';

/**
 * De weigerbare velden van een filterbalk, met hun labelsleutel — ook de sleutels die de API in
 * `details.issues[].path` noemt. Gedeeld door Processen en Orders (spec-orders-geweigerd-filter).
 */
export type FilterVelden = Readonly<Record<string, string>>;

/** De drie tekstsleutels per scherm, zodat Orders niet ongemerkt meeverandert met Processen. */
export interface FilterWeigerTeksten {
  melding: string;
  velden: string;
  wissen: string;
}

interface FoutBody {
  code?: string;
  details?: { issues?: { path?: unknown[] }[] };
}

function foutBody400(error: unknown): FoutBody | undefined {
  return isAxiosError<{ error?: FoutBody }>(error) && error.response?.status === 400 ? error.response.data?.error : undefined;
}

/** De filtervelden die het portaal zelf weigerde (400 `BAD_REQUEST`), zonder dubbelen. */
function geweigerdeVelden(error: unknown, velden: FilterVelden): string[] {
  const body = foutBody400(error);
  if (body?.code !== 'BAD_REQUEST') return [];
  const gevonden = (body.details?.issues ?? [])
    .map((issue) => issue.path?.[0])
    .filter((veld): veld is string => typeof veld === 'string' && Object.prototype.hasOwnProperty.call(velden, veld));
  return [...new Set(gevonden)];
}

/**
 * spec-statusfilter-processen CAP-2 / spec-orders-geweigerd-filter CAP-1: een filter dat de balk kan
 * herstellen is geweigerd — door de bron (`rejected_filter` of een doorgegeven 400) of door het
 * portaal op een filterveld. Een andere 400 (onbekende parameter, `page=0`) lost de balk niet op en
 * blijft een gewone fout. `bronStatus` is optioneel: Orders kent geen bronstatus in het antwoord;
 * Processen roept dit aan via `isProcessFilterRejected`, dat hem verplicht maakt.
 */
export function isFilterRejected(error: unknown, velden: FilterVelden, bronStatus?: string): boolean {
  return bronStatus === 'rejected_filter' || foutBody400(error)?.code === 'PROCESMANAGEMENT_REJECTED_FILTER' || geweigerdeVelden(error, velden).length > 0;
}

interface FilterRejectedMessageProps {
  rejected?: boolean;
  error?: unknown;
  velden?: FilterVelden;
  teksten?: FilterWeigerTeksten;
  /** Tweede invoer (spec-tabelfilters §7): ongeldige waarden uit een binnenkomende link, met hun kolomkop. */
  linkGeweigerd?: readonly LinkWeigering[];
  /** `processes` → testids `processes-filter-status`, `processes-filter-rejected` en `processes-filter-clear`. */
  baseTestId: string;
  onClear: () => void;
  /** Een element dat na het wissen blijft bestaan (de paginakop, `tabIndex={-1}`): de knop verdwijnt met de focus. */
  focusNaWissen: RefObject<HTMLElement | null>;
}

/**
 * Staat altijd op de pagina als lege live-regio; de melding verschijnt daarbinnen zodra het filter
 * geweigerd is. Een regio die tegelijk met zijn tekst verschijnt, wordt vaak niet voorgelezen.
 */
export function FilterRejectedMessage({ rejected = false, error, velden = {}, teksten, linkGeweigerd = [], baseTestId, onClear, focusNaWissen }: FilterRejectedMessageProps) {
  const { t } = useTranslation();
  const geweigerd = rejected ? geweigerdeVelden(error, velden) : [];
  const linkVelden = [...new Set(linkGeweigerd.map((w) => (w.kopSleutel ? t(w.kopSleutel) : w.parameter)))];
  const toon = rejected || linkVelden.length > 0;
  return (
    // `empty:absolute`: de lege regio neemt geen ruimte in de `space-y`/`gap` van de pagina, maar blijft in de toegankelijkheidsboom.
    <div aria-live="polite" className="empty:absolute" data-testid={`${baseTestId}-filter-status`} role="status">
      {toon ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background-muted)] p-4" data-testid={`${baseTestId}-filter-rejected`}>
          <p className="text-sm text-[var(--foreground)]">
            {rejected && teksten ? t(teksten.melding) : null}
            {rejected && teksten && geweigerd.length > 0 ? ` ${t(teksten.velden, { velden: geweigerd.map((veld) => (velden[veld] ? t(velden[veld]) : veld)).join(', ') })}` : null}
            {linkVelden.length > 0 ? `${rejected ? ' ' : ''}${t('tabel.ongeldigeLinkwaarde', { velden: linkVelden.join(', ') })}` : null}
          </p>
          <Button
            data-testid={`${baseTestId}-filter-clear`}
            onClick={() => {
              onClear();
              focusNaWissen.current?.focus();
            }}
            size="sm"
            variant="secondary"
          >
            {t(teksten?.wissen ?? 'tabel.standaardHerstellen')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
